import * as React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { useSelector } from "react-redux";
import { JSX } from "react";
import { Box } from "@mui/material";
import { useAppDispatch } from "./store/hooks";
import NavigationBar from "./containers/NavigationBar";
import Notifications from "./containers/FlashMessage/Notification";
import { getLanguage, setLanguage } from "../translations/translate";
import Modals from "./containers/Modals/Modals";
import { getAccount } from "./actions/user";
import Routes from "./Routes";
import "./styles/index.scss";
import ErrorBoundary from "./components/ErrorBoundary";
import StyleWrapper from "./StyleWrapper";
import OfflineMessage from "./components/OfflineMessage";
import { getServiceTags } from "./actions/serviceTags";
import MaintenanceModal from "./containers/Modals/MaintenanceModal/MaintenanceModal";
import HubspotChatBot from "./containers/hubspot/HubspotChatBot";
import ReleaseNoteModal from "./containers/Modals/ReleaseNoteModal/ReleaseNoteModal";
import ResourceCentreNotificationModal from "./containers/Modals/ResourceCentre/ResourceCentreNotificationModal";
import { RootState } from "./store";
import BottomBar from "./containers/NavigationBar/BottomNavBar";
import useLogoutListener from "./hooks/useLogoutListener";
import AnnouncementModal from "./containers/Modals/Announcement/AnnouncementModal";
import ClientPortalRoutes from "./client-portal/client-portal-routes";
import useClientPortal from "./hooks/useClientPortal";
import SystemTimeWarning from "./components/SystemTimeWarning";

type I18nContextProps = {
  language: string;
  changeLanguage: (lang) => void;
};

export const I18nContext = React.createContext<Partial<I18nContextProps>>({
  language: "np",
  changeLanguage: () => undefined
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

const App = (): JSX.Element => {
  useClientPortal();
  useLogoutListener();

  const authenticated = useSelector((state: RootState) => state.authenticated);
  const [showSupportChat, setShowSupportChat] = React.useState(false);
  const [unreadSupportMessageCount, setUnreadSupportMessageCount] = React.useState(0);
  const [state, setState] = React.useState({ language: getLanguage() || undefined });
  const dispatch = useAppDispatch();
  const isClientPortal = useSelector((rootState: RootState) => rootState.isClientPortal);

  React.useEffect(() => {
    if (!showSupportChat) {
      setUnreadSupportMessageCount(0);
    }
  }, [showSupportChat]);

  React.useEffect(() => {
    if (authenticated && !isClientPortal) {
      dispatch(getAccount());
      dispatch(getServiceTags());
    }
  }, [authenticated, dispatch, isClientPortal]);

  const changeLanguage = (language) => {
    setState({ language });
    setLanguage(language);
  };

  const { language } = state;

  const renderAppContent = () => {
    if (isClientPortal) {
      return <ClientPortalRoutes />;
    }
    return (
      <>
        <div style={{ display: "flex" }}>
          <SystemTimeWarning />

          <MaintenanceModal />
          {authenticated && (
            <>
              <ReleaseNoteModal />
              <AnnouncementModal />
              <ResourceCentreNotificationModal />
            </>
          )}
          <Modals />
          <Notifications />
          {authenticated && (
            <HubspotChatBot
              show={showSupportChat}
              onClose={() => {
                setShowSupportChat(false);
              }}
              onMessage={(message) => {
                setUnreadSupportMessageCount(message.unreadCount);
              }}
            />
          )}
          {authenticated && (
            <NavigationBar
              showLiveSupport={showSupportChat}
              setShowSupportChat={() => setShowSupportChat(!showSupportChat)}
              unreadSupportMessageCount={unreadSupportMessageCount}
            />
          )}
          <Box
            sx={(theme) => ({
              [theme.breakpoints.up("md")]: {
                marginLeft: "80px"
              },
              paddingTop: "50px",
              width: "100%",
              height: "100vh",
              overflowX: "hidden"
            })}
          >
            <Routes />
          </Box>
          {/* isRnWebView comes from injected javasriptt from rn app */}
          {window.isRnWebView && authenticated && <BottomBar />}
        </div>
      </>
    );
  };

  return (
    <QueryClientProvider client={queryClient}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <StyleWrapper>
          <ErrorBoundary>
            <OfflineMessage />
            <I18nContext.Provider value={{ language, changeLanguage }}>
              {renderAppContent()}
            </I18nContext.Provider>
          </ErrorBoundary>
        </StyleWrapper>
      </LocalizationProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
